import { Menu, MenuProps, Button, ActionIcon } from "@mantine/core"
import { IconMessageCirclePlus } from "@tabler/icons-react"
import { CreateChatModal } from "./CreateChatModal"
import { ChatType } from "@/@client/Theasoft_DataTypes_Enums"
import { useTranslation } from "react-i18next"
import { JoinPublicGroupModal } from "./JoinPublicGroupModal"
import { HoverAndTouchTooltip } from "@/routes/app/-components/hover-and-touch-tooltip"
import { useUserCanCreateChats } from "@/orval/gen"

const chatOptions: ChatOption[] = ["Chat", "Group", "JoinGroup", "Broadcast"]

type ChatOption = "Chat" | "Group" | "JoinGroup" | "Broadcast"

const feedOptions: FeedOption[] = ["Feed", "BroadcastFeed"]

type FeedOption = "Feed" | "BroadcastFeed"

function getChatTypeFromOption(
  option: Omit<ChatOption | FeedOption, "JoinGroup">
): ChatType {
  switch (option) {
    case "Chat":
      return "Direct"
    case "Group":
      return "PrivateGroup"
    case "Broadcast":
      return "Broadcast"
    case "Feed":
      return "Feed"
    case "BroadcastFeed":
      return "BroadcastFeed"
    default:
      return "Direct"
  }
}

interface Props extends MenuProps {
  label?: string
  variant: "chat" | "feed"
}

export function CreateChatMenu({ variant, label, ...props }: Props) {
  const [selectedOption, setSelectedOption] = React.useState<
    ChatOption | FeedOption
  >()
  const { t } = useTranslation("app_server")

  const options = variant === "chat" ? chatOptions : feedOptions
  const isChatVariant = variant === "chat"
  const { data: canCreateChat } = useUserCanCreateChats({
    query: { enabled: isChatVariant },
  })
  const canCreate = !isChatVariant || canCreateChat

  return (
    <>
      <Menu {...props} disabled={!canCreate}>
        <HoverAndTouchTooltip
          multiline
          disabled={canCreate}
          label={t("CantCreateChatTooltip")}
          children={
            <Menu.Target>
              {label ? (
                <Button
                  disabled={!canCreate}
                  justify="center"
                  rightSection={<IconMessageCirclePlus />}
                >
                  {isChatVariant ? t("NewChat") : t("NewFeed")}
                </Button>
              ) : (
                <ActionIcon disabled={!canCreate}>
                  <IconMessageCirclePlus />
                </ActionIcon>
              )}
            </Menu.Target>
          }
        />

        <Menu.Dropdown>
          {options.map((option) => (
            <Menu.Item key={option} onClick={() => setSelectedOption(option)}>
              {t(option)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      {selectedOption && selectedOption !== "JoinGroup" ? (
        <CreateChatModal
          onClose={() => setSelectedOption(undefined)}
          opened={true}
          chatType={getChatTypeFromOption(selectedOption)}
        />
      ) : null}
      {selectedOption && selectedOption === "JoinGroup" && (
        <JoinPublicGroupModal
          onClose={() => setSelectedOption(undefined)}
          opened={selectedOption === "JoinGroup"}
        />
      )}
    </>
  )
}
